import {
  AgentBackgroundSoundsRequestDto,
  AgentBackgroundSoundsResponseDto,
  AmbientSoundResponseDto,
  BackgroundSoundListItemResponseDto,
  CampaignBackgroundSoundsRequestDto,
  CampaignBackgroundSoundsResponseDto,
  ThinkingSoundResponseDto,
} from '@shared/services/apiService/apiService';

const DEFAULT_AMBIENT_VOLUME = 50;
const DEFAULT_THINKING_PROBABILITY = 10;
let nextThinkingSoundClientId = 0;

const createThinkingSoundClientId = () => `thinking-sound-${++nextThinkingSoundClientId}`;

export type AmbientSoundDraft = {
  soundId: string | null;
  volume: number;
  previousVolume: number;
  missing: boolean;
};

export type ThinkingSoundDraft = {
  clientId: string;
  soundId: string;
  volume: number;
  previousVolume: number;
  probability: number;
  missing: boolean;
};

export type AmbienceSettingsDraft = {
  overrideAmbientSound: boolean;
  overrideThinkingSounds: boolean;
  ambientSound: AmbientSoundDraft | null;
  thinkingSounds: ThinkingSoundDraft[];
};

export const clampPercentage = (value: number) =>
  Math.max(0, Math.min(100, Math.round(Number.isFinite(value) ? value : 0)));

const toRequestVolume = (value: number) => clampPercentage(value) / 100;
const toRequestProbability = (value: number) => clampPercentage(value) / 100;
const uniqueBySoundId = <T extends { soundId: string }>(items: T[]): T[] => {
  const seen = new Set<string>();

  return items.filter((item) => {
    if (!item.soundId || seen.has(item.soundId)) {
      return false;
    }

    seen.add(item.soundId);
    return true;
  });
};

export const normalizeVolume = (value?: number | null, fallback = DEFAULT_AMBIENT_VOLUME) => {
  if (value === null || value === undefined) {
    return clampPercentage(fallback);
  }

  return clampPercentage(value <= 1 ? value * 100 : value);
};

export const normalizeProbability = (
  value?: number | null,
  fallback = DEFAULT_THINKING_PROBABILITY,
) => {
  if (value === null || value === undefined) {
    return clampPercentage(fallback);
  }

  return clampPercentage(value <= 1 ? value * 100 : value);
};

export const getSoundDefaultVolume = (sound?: BackgroundSoundListItemResponseDto | null) =>
  normalizeVolume(sound?.defaultVolume, DEFAULT_AMBIENT_VOLUME);

export const createEmptyAmbienceDraft = (): AmbienceSettingsDraft => ({
  overrideAmbientSound: false,
  overrideThinkingSounds: false,
  ambientSound: null,
  thinkingSounds: [],
});

export const createAmbientSoundDraft = (
  sound?: BackgroundSoundListItemResponseDto | null,
): AmbientSoundDraft => {
  const defaultVolume = getSoundDefaultVolume(sound);

  return {
    soundId: sound?.id ?? null,
    volume: defaultVolume,
    previousVolume: defaultVolume,
    missing: false,
  };
};

export const createThinkingSoundDraft = (
  sound?: BackgroundSoundListItemResponseDto | null,
  overrides?: Partial<ThinkingSoundDraft>,
): ThinkingSoundDraft => {
  const defaultVolume = getSoundDefaultVolume(sound);
  const volume =
    overrides?.volume === undefined ? defaultVolume : clampPercentage(overrides.volume);
  const previousVolume =
    overrides?.previousVolume === undefined
      ? defaultVolume
      : clampPercentage(overrides.previousVolume);

  return {
    clientId: overrides?.clientId ?? createThinkingSoundClientId(),
    soundId: overrides?.soundId ?? sound?.id ?? '',
    volume,
    previousVolume,
    probability:
      overrides?.probability === undefined
        ? DEFAULT_THINKING_PROBABILITY
        : clampPercentage(overrides.probability),
    missing: overrides?.missing ?? false,
  };
};

const resolveSoundId = (sound: AmbientSoundResponseDto | ThinkingSoundResponseDto | undefined) => {
  const ref = sound?.sound;

  if (!ref) {
    return null;
  }

  if ('id' in ref) {
    return ref.id;
  }

  return null;
};

export const mapBackgroundSoundsToDraft = (
  backgroundSounds:
    | Pick<
        CampaignBackgroundSoundsResponseDto,
        'ambientSound' | 'thinkingSounds' | 'overrideAmbientSound' | 'overrideThinkingSounds'
      >
    | Pick<AgentBackgroundSoundsResponseDto, 'ambientSound' | 'thinkingSounds'>
    | null
    | undefined,
  sounds: BackgroundSoundListItemResponseDto[],
  overrides?: Pick<AmbienceSettingsDraft, 'overrideAmbientSound' | 'overrideThinkingSounds'>,
): AmbienceSettingsDraft => {
  const emptyDraft = createEmptyAmbienceDraft();

  if (!backgroundSounds) {
    return {
      ...emptyDraft,
      ...overrides,
    };
  }

  const ambientSoundId = resolveSoundId(backgroundSounds.ambientSound);
  const ambientSound = ambientSoundId
    ? (sounds.find((sound) => sound.id === ambientSoundId) ?? null)
    : null;

  const thinkingSounds = uniqueBySoundId(
    (backgroundSounds.thinkingSounds ?? [])
      .map((thinkingSound: ThinkingSoundResponseDto) => {
        const soundId = resolveSoundId(thinkingSound);

        if (!soundId) {
          return null;
        }

        const sound = sounds.find((item) => item.id === soundId);
        const normalizedVolume = normalizeVolume(
          thinkingSound.volume,
          sound ? getSoundDefaultVolume(sound) : DEFAULT_AMBIENT_VOLUME,
        );

        return createThinkingSoundDraft(sound, {
          soundId,
          volume: normalizedVolume,
          previousVolume:
            normalizedVolume > 0
              ? normalizedVolume
              : sound
                ? getSoundDefaultVolume(sound)
                : DEFAULT_AMBIENT_VOLUME,
          probability: normalizeProbability(thinkingSound.probability),
          missing: !sound,
        });
      })
      .filter((item): item is ThinkingSoundDraft => item !== null),
  );

  return {
    overrideAmbientSound:
      overrides?.overrideAmbientSound ??
      ('overrideAmbientSound' in backgroundSounds &&
        Boolean(backgroundSounds.overrideAmbientSound)),
    overrideThinkingSounds:
      overrides?.overrideThinkingSounds ??
      ('overrideThinkingSounds' in backgroundSounds &&
        Boolean(backgroundSounds.overrideThinkingSounds)),
    ambientSound: ambientSoundId
      ? {
          soundId: ambientSoundId,
          volume: normalizeVolume(
            backgroundSounds.ambientSound?.volume,
            ambientSound ? getSoundDefaultVolume(ambientSound) : DEFAULT_AMBIENT_VOLUME,
          ),
          previousVolume: normalizeVolume(
            backgroundSounds.ambientSound?.volume,
            ambientSound ? getSoundDefaultVolume(ambientSound) : DEFAULT_AMBIENT_VOLUME,
          ),
          missing: !ambientSound,
        }
      : null,
    thinkingSounds,
  };
};

export const cloneAmbienceDraft = (draft: AmbienceSettingsDraft): AmbienceSettingsDraft => ({
  overrideAmbientSound: draft.overrideAmbientSound,
  overrideThinkingSounds: draft.overrideThinkingSounds,
  ambientSound: draft.ambientSound ? { ...draft.ambientSound } : null,
  thinkingSounds: draft.thinkingSounds.map((item) => ({
    ...item,
    clientId: item.clientId ?? createThinkingSoundClientId(),
  })),
});

export const stripAmbienceDraft = (draft: AmbienceSettingsDraft) => ({
  overrideAmbientSound: draft.overrideAmbientSound,
  overrideThinkingSounds: draft.overrideThinkingSounds,
  ambientSound: draft.ambientSound
    ? {
        soundId: draft.ambientSound.soundId,
        volume: clampPercentage(draft.ambientSound.volume),
      }
    : null,
  thinkingSounds: uniqueBySoundId(draft.thinkingSounds).map((item) => ({
    soundId: item.soundId,
    volume: clampPercentage(item.volume),
    probability: clampPercentage(item.probability),
  })),
});

const mapDraftSoundsToRequest = (draft: AmbienceSettingsDraft) => ({
  ambientSound: draft.ambientSound?.soundId
    ? {
        soundId: draft.ambientSound.soundId,
        volume: toRequestVolume(draft.ambientSound.volume),
      }
    : null,
  thinkingSounds: uniqueBySoundId(draft.thinkingSounds).map((item) => ({
    soundId: item.soundId,
    volume: toRequestVolume(item.volume),
    probability: toRequestProbability(item.probability),
  })),
});

export const mapCampaignAmbienceDraftToRequest = (
  draft: AmbienceSettingsDraft,
): CampaignBackgroundSoundsRequestDto => ({
  overrideAmbientSound: draft.overrideAmbientSound,
  overrideThinkingSounds: draft.overrideThinkingSounds,
  ...mapDraftSoundsToRequest(draft),
});

export const mapAgentAmbienceDraftToRequest = (
  draft: AmbienceSettingsDraft,
): AgentBackgroundSoundsRequestDto => ({
  ...mapDraftSoundsToRequest(draft),
});

export const getThinkingProbabilityTotal = (draft: AmbienceSettingsDraft) =>
  draft.thinkingSounds.reduce((sum, item) => sum + clampPercentage(item.probability), 0);

export const hasMissingSounds = (draft: AmbienceSettingsDraft) =>
  Boolean(draft.ambientSound?.missing) || draft.thinkingSounds.some((item) => item.missing);
